import Head from 'next/head'
import Hero from 'components/folds/common/hero-fold'
import { seedCache, fetchAllPostsOfType } from 'lib/wp-fetcher'
import { Container, Row, Col } from 'react-bootstrap'
import { useRouter } from 'next/router'
import LoadingPage from 'components/folds/common/loading'
import HtmlText from 'atoms/html-text'
import CtaFold from 'components/folds/common/cta-fold'
import SuccessItems from 'components/folds/succesverhalen-items'
import { fetchCtasForPost, fetchGeneralCtas } from 'lib/cta-fetcher'
import ReviewsFold from 'components/folds/common/reviews-fold'
import AuthorFold from 'components/folds/common/author-fold'
import { fetchRecentReviews, fetchReviewsSummary } from 'lib/feedbackcompany-fetcher'
import { fetchAuthor } from 'lib/author'
import { cachedFetch } from 'lib/cached-fetch'
import { wikiPostTypeSlugMapping } from 'lib/wiki-cpts'
import { fetchPostIdsOfType } from '../../lib/wp-fetcher'
import Video from 'components/folds/common/single-video-fold'
import { RankMathData } from 'lib/rank-math'
import parse from 'html-react-parser'

export default function SuccessPage({
  article,
  author,
  featuredMedia,
  generalCtaPosts,
  ctaPosts,
  reviewData,
  successArticles,
  rankMathHead
}) {
  const router = useRouter();

  if(router.isFallback) {
    return <LoadingPage />
  }

  const renderIntro = (extraClasses = '') => (<>
    {article.mainVideo &&
      <aside className={`mb-0 mb-sm-3 ${extraClasses}`}>
        <Video url={article.mainVideo.acf.url} />
        {/* {featuredMedia && <HtmlText body={article?.excerpt.rendered} />}
        {featuredMedia && <Image
          src={featuredMedia.source_url}
          width={featuredMedia.media_details.width}
          height={featuredMedia.media_details.height}
          objectFit="contain"
        />} */}
      </aside>
    }
  </>)

  return (
    <div>
      <Head>
        {article?.link ? (
          parse(rankMathHead)
        ) : (
          <>
            <title>Fysiotherapie4all - {article?.title?.rendered}</title>
            <meta name="description" content={article?.excerpt.rendered} />
          </>
        )}
        {article && <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": article.link,
            },
            "headline": article.title?.rendered,
            "description": article.excerpt?.rendered,
            "image": featuredMedia?.source_url,
            "author": {
              "@type": "Person",
              "name": author.name,
            },
            "publisher": {
              "@type": "Organization",
              "name": "Fysiotherapie4all",
              "logo": {
                "@type": "ImageObject",
                "url": "https://fysiotherapie4all.nl/img/logo.png"
              }
            },
            "datePublished": article.date,
            "dateModified": article.modified,
          })}}
        />}
      </Head>
      <main>
        <Hero
          title={article?.title?.rendered}
          minHeight={50}
          dynamicTitleFontSize
        />
        <Container>
          <Row>
            <Col className={`col-12 ${article.mainVideo ? 'order-1 order-lg-2' : 'order-2 order-lg-1'} col-lg-3 mb-3`}>
              {renderIntro('d-block d-lg-none')}

              <aside className="sticky-container">  
                <CtaFold ctaPosts={ctaPosts} asSideItem />
              </aside>
            </Col>
            <Col className={`col-12 ${article.mainVideo ? 'order-2' : 'order-1'} order-lg-2 hide-toc col-lg-6`}>
              <div className="d-lg-block">{renderIntro('d-none d-lg-block')}</div>
              <article>
               <HtmlText body={article?.content?.rendered} />
              </article>

              <div className="mt-5">
                <AuthorFold author={article.author} />
              </div>
            </Col>
            <Col className="col-12 order-3 col-lg-3 order-lg-3 mb-3">
              <aside className="sticky-container">
                <SuccessItems
                  title="Meer succesverhalen"
                  articles={successArticles}
                />
              </aside>
            </Col>
          </Row>
          
        </Container>
        <CtaFold ctaPosts={generalCtaPosts} fullWidth />
        <ReviewsFold
          background
          noSkew
          reviews={reviewData.reviews}
          summary={reviewData.summary}
        />
      </main>

      <style global jsx>{`
        .hide-toc .lwptoc {
          display: none;
        }

        .single-video-fold .row {
          padding-top: 0;
          padding-bottom: 30px;
        }

        .sticky-container {
          position: -webkit-sticky; /* Safari */
          position: sticky;
          top: 110px;
        }

        @media screen and (min-width: 992px) and (max-width: 1199px) {
          .sticky-container {
            top: 150px;
          }
        }

        @media screen and (min-width: 1200px) {
          main .container{
            max-width: 1400px;
          }
        }
      `}</style>
    </div>
  )
}

export async function getStaticPaths() {
  // Let's seed the cache a bit first!
  await seedCache();

  const paths = [];

  for await (const type of wikiPostTypeSlugMapping()) {
    const posts = await fetchAllPostsOfType(type.apiSlug);
    posts
      .map(post => {
        return {
          params: {
            id: post.id.toString(),
            slug: post.slug,
            articleType: type.slug,
            post,
          },
        }
      })
      .forEach(post => paths.push(post))
  }

  return { paths, fallback: true }
}

export async function getStaticProps({ params }) {
  if (!params.articleType && !params.slug) {
    return {
      props: {}
    }
  }

  const postTypeForSlug = wikiPostTypeSlugMapping().find(type => type.slug === params.articleType)?.apiSlug;
  const posts = await cachedFetch(`${process.env.NEXT_PUBLIC_API_URL}/wp-json/wp/v2/success/?slug=${params.slug}`)

  //console.log(`${process.env.NEXT_PUBLIC_API_URL}/wp-json/wp/v2/success/?slug=${params.slug}`);
  
  if (posts.length < 1) {
    return {
      notFound: true,
      revalidate: parseInt(process.env.REVALIDATION_TIME_SECONDS),
    }
  }

  const article = posts[0]

  let author = { name: 'Maikel Korbmacher' }
  let featuredMedia = null
  if (article) {
    author = await cachedFetch(article._links.author[0].href)
    
    if (article.featured_media !== 0) {
      const url = article._links['wp:featuredmedia'][0].href;
      try {
        featuredMedia = await cachedFetch(url)
      } catch (error) {
        console.error(`Error fetching media: ${url} is restricted.`)
      }
    }
  }

  let ctaPosts = []
  try {
    ctaPosts = (await fetchCtasForPost(article.id)).slice(0, 1)
  } catch (error) {
    console.error(`Could not fetch CTAs for post ID ${article.id} (${params.articleType})`)
  }

  if (article.acf.main_video) {
    article.mainVideo = (await fetchPostIdsOfType(article.acf.main_video, 'videos'))[0]
  }

  const successArticles = await cachedFetch(`${process.env.NEXT_PUBLIC_API_URL}/wp-json/wp/v2/success/`)

  article.author = await fetchAuthor(article);

  return {
    props: { 
      article,
      author,
      featuredMedia,
      generalCtaPosts: await fetchGeneralCtas(),
      ctaPosts,
      reviewData: {
        reviews: await fetchRecentReviews(),
        summary: await fetchReviewsSummary(),
      },
      successArticles,
      rankMathHead: await RankMathData(article?.link),
    },
    revalidate: parseInt(process.env.REVALIDATION_TIME_SECONDS),
  }
}
